import { loadGoogleMapsScript } from '../hooks/useGoogleMapsLoader'

/**
 * Where a vehicle is, in words. "12.5942, -8.0319" tells a dispatcher nothing;
 * "Baco Djikoroni, Bamako" tells them everything.
 *
 * Resolution is on demand and cached: a coordinate rounded to four decimals
 * (about 11 m) is the cache key, and concurrent callers for the same key share
 * one request, so opening a vehicle card costs at most one geocoding call and
 * re-opening it costs none.
 */

const cache = new Map<string, string | null>()
const inFlight = new Map<string, Promise<string | null>>()

const keyFor = (latitude: number, longitude: number) =>
  `${latitude.toFixed(4)},${longitude.toFixed(4)}`

// From the most specific place a driver would name to the least. The street is
// prepended when known, because "Rue 581, Baco Djikoroni" locates a stop far
// better than the district alone.
const AREA_TYPES = [
  'neighborhood',
  'sublocality',
  'sublocality_level_1',
  'locality',
  'postal_town',
  'administrative_area_level_2',
  'administrative_area_level_1'
]

const componentOfType = (
  components: google.maps.GeocoderAddressComponent[],
  type: string
) => components.find(component => component.types.includes(type))?.short_name

function describe(result?: google.maps.GeocoderResult): string | null {
  if (!result) return null
  const components = result.address_components || []
  const area = AREA_TYPES.map(type => componentOfType(components, type)).find(Boolean)
  const route = componentOfType(components, 'route')
  const parts = [route, area].filter(Boolean)
  if (parts.length) return parts.join(', ')
  // Plus codes and raw coordinates are no better than what the caller already
  // shows, so they do not count as a name.
  const formatted = result.formatted_address || ''
  return /^[0-9A-Z]{4}\+/.test(formatted) || !formatted ? null : formatted
}

let geocoder: google.maps.Geocoder | null = null

async function geocode(latitude: number, longitude: number): Promise<string | null> {
  // The vehicle list can be open while the map window is closed, so the script
  // is ensured here rather than assumed.
  await loadGoogleMapsScript([], 1, 500)
  if (!geocoder) geocoder = new google.maps.Geocoder()
  // Callback form, as in CorridorMap: it works on every version of the API this
  // app has been loaded with.
  const results = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
    geocoder!.geocode({ location: { lat: latitude, lng: longitude } }, (found, status) => {
      if (status === 'OK' && found) resolve(found)
      else reject(new Error(String(status)))
    })
  })
  return describe(results[0])
}

/** The place name, or null when it cannot be resolved — never a fake one. */
export async function resolvePlaceName(
  latitude: number,
  longitude: number
): Promise<string | null> {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
  const key = keyFor(latitude, longitude)
  if (cache.has(key)) return cache.get(key) ?? null

  const pending = inFlight.get(key)
  if (pending) return pending

  const request = geocode(latitude, longitude)
    .catch(error => {
      console.warn('Reverse geocoding failed, keeping the coordinates:', error)
      return null
    })
    .then(name => {
      cache.set(key, name)
      inFlight.delete(key)
      return name
    })

  inFlight.set(key, request)
  return request
}

/** Already-known name, for a first render without waiting on the geocoder. */
export const cachedPlaceName = (latitude: number, longitude: number) =>
  cache.get(keyFor(latitude, longitude)) ?? null
